import * as React from 'react'
import {
  Button,
  Drawer,
  Row,
  Col,
  Descriptions,
  Alert,
  Input,
  InputNumber,
  Select,
  message,
  Tooltip,
} from 'antd'
import {
  IBaseProduct,
  IBatchResponseWithStatus,
  IBatchResponse,
  API,
  PermissionNames,
} from '@getgreenline/homi-shared'
import { InventoryLogType } from '@getgreenline/inventory/build/models/inventoryLog'
import { EditLotNumberModal } from './EditLotNumberModal'
import { InventoryLogGraph } from './InventoryLogGraph'
import { EventLogs } from './EventLogs'
import { Routes } from '../../../../../../constants/Routes'
import { GrPrice, parseErrorMsg, removeDecimalPoint } from '../../../../../../utilities/helpers'
import { BlazeButton } from '../../../../../../components/CustomButtons/BlazeButton'
import { SmartImage } from '../../../../../../components/SmartImage'
import { AvailableBatchSelect } from '../../../../../../components/AvailableBatchSelect'
import { UpdateInventory } from './UpdateInventory'
import { observer, inject } from 'mobx-react'
import { InventoryLogStore } from './InventoryLogStore'
import { CurrentUserStore } from '../../../../../../stores/CurrentUserStore'
import { TooltipWrapper } from './TooltipWrapper'
import { isNil } from 'lodash'

interface Props {
  product: IBaseProduct
  locationId: number
  companyId: number
  mode: 'add' | 'subtract' | 'set' | undefined
  batches: IBatchResponseWithStatus[]
  selectedBatchId: number | undefined
  quantity: number
  lowStockQuantity?: number
  reStockQuantity?: number
  selectReason: Reason
  quantitySetTo: number | undefined
  updateInventory: UpdateInventory
  inventoryLogStore: InventoryLogStore

  onClose: () => void
  toggleActionButtons: (action: undefined | 'add' | 'subtract' | 'set') => void
  onApply: () => void
  refreshBatches: () => Promise<void>
}

interface InjectedProps {
  currentUserStore?: CurrentUserStore
}

interface State {
  isSettingQuantity: boolean
  isApplyingLowStockQty: boolean
  isApplyingReStockQty: boolean
  showEditLotNumberModal: boolean
  selectedBatch?: IBatchResponse
  windowWidth: number
  lowStockQuantity?: number
  restockQuantity?: number
}

interface Reason {
  value: InventoryLogType | undefined
  options: InventoryLogType[]
  onChange: (value: InventoryLogType) => void
}

const productHref = (product: IBaseProduct) => {
  const productIdToLinkTo = product.parentProductId ? product.parentProductId : product.id
  return Routes.EDIT_PRODUCT(product.companyId, productIdToLinkTo)
}

const numberFormatter = (value: string) => {
  if (value.charAt(0) === '-') value = value.slice(1)
  //only allow decimals and integers
  const numDecReg = new RegExp(/^\d*\.?\d*$/)
  if (numDecReg.test(value)) {
    return value
  }
  return undefined
}

const formatter = (value: string | number | undefined, sign?: '+' | '-') => {
  const validatedValue = value !== undefined ? numberFormatter(value.toString()) : undefined
  const formattedVal = validatedValue ? `${sign || ''}${validatedValue}` : `${sign || ''}`

  return formattedVal
}

@inject('currentUserStore')
@observer
export class InventoryDrawer extends React.Component<Props & InjectedProps, State> {
  thresholdWidth = 830
  NO_PERMISSION_TITLE = `You don't have 'Can edit inventory' permissions`

  constructor(props: Props) {
    super(props)
    this.state = {
      isSettingQuantity: false,
      isApplyingLowStockQty: false,
      isApplyingReStockQty: false,
      showEditLotNumberModal: false,
      selectedBatch: undefined,
      windowWidth: window.innerWidth,
      restockQuantity: props.reStockQuantity,
      lowStockQuantity: props.lowStockQuantity,
    }
  }

  componentDidMount = () => {
    this.updateWindowDimensions()
    window.addEventListener('resize', this.updateWindowDimensions)
  }

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.updateWindowDimensions)
  }

  updateWindowDimensions = () => this.setState({ windowWidth: window.innerWidth })

  onLotClick = (batch: IBatchResponse) =>
    this.setState({
      selectedBatch: batch,
      showEditLotNumberModal: true,
    })

  updateLowStock = async () => {
    try {
      this.setState({ isApplyingLowStockQty: true })

      const { companyId, locationId, product, updateInventory } = this.props

      const lowStockQuantity =
        this.state.lowStockQuantity === undefined ? null : this.state.lowStockQuantity

      await API.updateProductStockAlertLevels(companyId, locationId, product.id, {
        lowStockQuantity,
      })

      updateInventory.setNewLowStockQuantity(lowStockQuantity)

      message.success('Low stock quantity has been updated', 2)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while updating low-stock quantity')
    } finally {
      this.setState({ isApplyingLowStockQty: false })
    }
  }

  updateReStock = async () => {
    try {
      this.setState({ isApplyingReStockQty: true })

      const { companyId, locationId, product, updateInventory } = this.props

      const restockQuantity =
        this.state.restockQuantity === undefined ? null : this.state.restockQuantity

      await API.updateProductStockAlertLevels(companyId, locationId, product.id, {
        restockQuantity,
      })

      updateInventory.setNewReStockQuantity(restockQuantity)

      message.success('Re stock quantity has been updated', 2)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while updating re-stock quantity')
    } finally {
      this.setState({ isApplyingReStockQty: false })
    }
  }

  onButtonClick = async () => {
    try {
      await this.setState({ isSettingQuantity: true })
      await this.props.onApply()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while updating quantity')
    } finally {
      await this.setState({ isSettingQuantity: false })
    }
  }

  get descriptionLayout(): 'horizontal' | 'vertical' {
    return this.state.windowWidth < this.thresholdWidth ? 'vertical' : 'horizontal'
  }

  get drawerWidth(): number {
    return this.state.windowWidth < this.thresholdWidth
      ? this.state.windowWidth
      : this.thresholdWidth
  }

  get isButtonDisabled(): boolean {
    const { mode, selectReason, quantitySetTo, product, selectedBatchId, updateInventory } =
      this.props
    const { isSettingQuantity } = this.state

    const isInvalidState =
      mode === 'subtract'
        ? !selectReason.value ||
          quantitySetTo === undefined ||
          (product.isBatchTracked && !selectedBatchId) ||
          isSettingQuantity
        : quantitySetTo === undefined ||
          (product.isBatchTracked && !selectedBatchId) ||
          isSettingQuantity

    const isValueZero = mode !== 'set' && updateInventory.quantity === 0

    return isInvalidState || isValueZero
  }

  get alertMessage(): string {
    const { mode } = this.props

    switch (mode) {
      case 'add':
        return 'Purchase orders recommended'
      case 'subtract':
        return 'Select a reason'
      case 'set':
        return 'Reset your inventory'
      default:
        return ''
    }
  }

  get alertDescription(): JSX.Element | undefined {
    const { mode, companyId } = this.props

    switch (mode) {
      case 'add':
        return (
          <p className='mb-0'>
            Use this only to make inventory corrections. To track compliance and costs accurately,
            we recommend using{' '}
            <a target='_blank' href={Routes.DASHBOARD_INVOICES(companyId)} rel='noreferrer'>
              purchase orders.
            </a>
          </p>
        )
      case 'subtract':
        return (
          <ul className='mb-0'>
            <li>Audit (you are not quite sure why this is missing)</li>
            <li>Destroyed (you have physically destroyed the product)</li>
            <li>Lost (you can no longer find this unit in your store)</li>
            <li>Return (this was sent back to the wholesale/distributor)</li>
          </ul>
        )
      case 'set':
        return (
          <p className='mb-0'>
            Enter your new inventory value. This will overwrite your current inventory. For bulk
            inventory counts, please use the{' '}
            <a target='_blank' href={Routes.DASHBOARD_INVENTORY_AUDIT(companyId)} rel='noreferrer'>
              audits
            </a>{' '}
            feature.
          </p>
        )
      default:
        return
    }
  }

  get inputFormatter(): ((value?: string | number) => string) | undefined {
    const { mode } = this.props
    let sign: '+' | '-' | undefined

    switch (mode) {
      case 'add':
        sign = '+'
        break
      case 'subtract':
        sign = '-'
        break
      default:
        sign = undefined
    }

    return (value) => formatter(this.removeDecimal(value), sign)
  }

  get inputMax(): number | undefined {
    return this.props.mode === 'subtract' ? 0 : undefined
  }

  get inputMin(): number | undefined {
    return this.props.mode !== 'subtract' ? 0 : undefined
  }

  get selectDefault(): InventoryLogType.AUDIT | undefined {
    return this.props.mode === 'subtract' ? undefined : InventoryLogType.AUDIT
  }

  removeDecimal = (value: number | string | undefined | null) => {
    if (value === '' || isNil(value)) {
      return
    }

    const newVal = removeDecimalPoint(value)

    return isNaN(newVal) ? undefined : newVal
  }

  render() {
    const {
      product,
      quantity,
      toggleActionButtons,
      mode,
      onClose,
      companyId,
      locationId,
      selectReason,
      selectedBatchId,
      batches,
      updateInventory,
    } = this.props
    const {
      isSettingQuantity,
      isApplyingLowStockQty,
      isApplyingReStockQty,
      restockQuantity,
      lowStockQuantity,
    } = this.state
    const { showEditLotNumberModal, selectedBatch } = this.state
    const canEditInventoryForLocation = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_INVENTORY,
      locationId,
    )

    return (
      <>
        <Drawer
          width={this.drawerWidth}
          title='Inventory'
          visible={true}
          closable={true}
          onClose={onClose}
        >
          <Row className='d-flex mb-4'>
            <Col span={4}>
              <SmartImage
                className='img-thumbnail mr-2'
                src={product.imageUrl}
                width={100}
                height={100}
              />
            </Col>

            <Col className='ml-2'>
              <b>
                <span>
                  <a target='_blank' href={productHref(product)} rel='noreferrer'>
                    {product.parentProductName || product.name}
                  </a>

                  {product.parentProductName && ` - ${product.name}`}
                </span>
              </b>

              <div>
                <span className='text-muted'>SKU: {product.sku || '-'}</span>
              </div>

              <div>
                <span className='text-muted'>Barcode: {product.barcode || '-'}</span>
              </div>

              <div>
                <span className='text-muted'>
                  Price: {product.price ? GrPrice.convertCentToDollar(product.price) : '-'}
                </span>
              </div>
            </Col>
          </Row>

          <Descriptions
            className='mb-4'
            title=''
            column={1}
            layout={this.descriptionLayout}
            bordered
          >
            <Descriptions.Item label='Quantity'>
              <div className='d-flex flex-wrap align-items-center'>
                <span className='mr-3' style={{ fontSize: 20, fontWeight: 500 }}>
                  {quantity}
                </span>

                <span className='d-flex flex-wrap align-items-center'>
                  <TooltipWrapper
                    renderTooltip={!canEditInventoryForLocation}
                    tooltipProps={{
                      title: this.NO_PERMISSION_TITLE,
                    }}
                  >
                    <BlazeButton
                      buttonProps={{
                        icon: 'plus',
                        className: 'mr-2 mt-1 mb-1',
                        onClick: () => toggleActionButtons('add'),
                        disabled: !canEditInventoryForLocation,
                      }}
                      titleProps={{
                        title: 'Add',
                      }}
                    />

                    <BlazeButton
                      buttonProps={{
                        icon: 'minus',
                        className: 'mr-2 mt-1 mb-1',
                        onClick: () => toggleActionButtons('subtract'),
                        disabled: !canEditInventoryForLocation,
                      }}
                      titleProps={{
                        title: 'Remove',
                      }}
                    />

                    <BlazeButton
                      buttonProps={{
                        icon: 'redo',
                        className: 'mr-2 mt-1 mb-1',
                        onClick: () => toggleActionButtons('set'),
                        disabled: !canEditInventoryForLocation,
                      }}
                      titleProps={{
                        title: 'Set',
                      }}
                    />
                  </TooltipWrapper>
                </span>
              </div>
            </Descriptions.Item>

            {mode && (
              <Descriptions.Item label='Action'>
                <Alert
                  message={this.alertMessage}
                  className='mb-3'
                  description={this.alertDescription}
                  type='warning'
                />

                <Input.Group compact className='mb-3'>
                  <InputNumber
                    className='w-25'
                    formatter={this.inputFormatter}
                    max={this.inputMax}
                    min={this.inputMin}
                    onChange={(value) => updateInventory.setQuantity(this.removeDecimal(value))}
                  />

                  <Select<InventoryLogType>
                    style={{ width: '150px' }}
                    placeholder='Select a reason'
                    defaultValue={this.selectDefault}
                    value={selectReason.value}
                    onChange={(value) => selectReason.onChange(value)}
                  >
                    {selectReason.options.map((type, index) => (
                      <Select.Option key={index} value={type}>
                        {type}
                      </Select.Option>
                    ))}
                  </Select>

                  <BlazeButton
                    buttonProps={{
                      type: 'primary',
                      loading: isSettingQuantity,
                      disabled: this.isButtonDisabled,
                      onClick: this.onButtonClick,
                    }}
                    titleProps={{ title: 'Apply ' }}
                  />
                </Input.Group>

                {product.isBatchTracked && (
                  <AvailableBatchSelect
                    wrapperClassName='mb-3'
                    companyId={companyId}
                    locationId={locationId}
                    productId={product.id}
                    selectedBatchId={selectedBatchId}
                    onSelect={(value) => updateInventory.setBatchId(value)}
                  />
                )}

                <Input.TextArea
                  rows={2}
                  placeholder='Add notes'
                  onChange={(e) => updateInventory.setNotes(e.target.value)}
                />
              </Descriptions.Item>
            )}

            {product.isBatchTracked && (
              <Descriptions.Item label='Lots'>
                <table>
                  <thead>
                    <tr>
                      <th>Lot #</th>
                      <th className='text-right'>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    <TooltipWrapper
                      renderTooltip={!canEditInventoryForLocation}
                      tooltipProps={{
                        title: this.NO_PERMISSION_TITLE,
                      }}
                    >
                      {batches.map((batch) => {
                        return (
                          <tr key={batch.id}>
                            <td>
                              {canEditInventoryForLocation ? (
                                <a onClick={() => this.onLotClick(batch)}>{batch.batchNumber}</a>
                              ) : (
                                <>{batch.batchNumber}</>
                              )}
                            </td>
                            <td className='text-right'>{batch.quantityRemaining}</td>
                          </tr>
                        )
                      })}
                    </TooltipWrapper>
                  </tbody>
                </table>
              </Descriptions.Item>
            )}

            <Descriptions.Item label='Low stock qty'>
              <TooltipWrapper
                renderTooltip={!canEditInventoryForLocation}
                tooltipProps={{
                  title: this.NO_PERMISSION_TITLE,
                }}
              >
                <InputNumber
                  className='mr-2'
                  formatter={(value) => formatter(this.removeDecimal(value))}
                  value={lowStockQuantity}
                  min={0}
                  onChange={(value) =>
                    this.setState({ lowStockQuantity: this.removeDecimal(value) })
                  }
                  disabled={!canEditInventoryForLocation}
                />

                <Button
                  loading={isApplyingLowStockQty}
                  onClick={this.updateLowStock}
                  disabled={!canEditInventoryForLocation}
                >
                  Save
                </Button>
              </TooltipWrapper>
            </Descriptions.Item>

            <Descriptions.Item label='Restock qty'>
              <TooltipWrapper
                renderTooltip={!canEditInventoryForLocation}
                tooltipProps={{
                  title: this.NO_PERMISSION_TITLE,
                }}
              >
                <InputNumber
                  className='mr-2'
                  formatter={(value) => formatter(this.removeDecimal(value))}
                  value={restockQuantity}
                  min={0}
                  onChange={(value) =>
                    this.setState({ restockQuantity: this.removeDecimal(value) })
                  }
                  disabled={!canEditInventoryForLocation}
                />

                <Button
                  loading={isApplyingReStockQty}
                  onClick={this.updateReStock}
                  disabled={!canEditInventoryForLocation}
                >
                  Save
                </Button>
              </TooltipWrapper>
            </Descriptions.Item>
          </Descriptions>

          <InventoryLogGraph
            inventoryLogStore={this.props.inventoryLogStore}
            companyId={companyId}
            locationId={locationId}
            productId={product.id}
            defaultQuantity={quantity}
          />

          {showEditLotNumberModal && selectedBatch && (
            <EditLotNumberModal
              companyId={companyId}
              productId={product.id}
              locationId={locationId}
              batch={selectedBatch}
              onClose={async () => {
                this.setState({ showEditLotNumberModal: false })
                await this.props.refreshBatches()
              }}
            />
          )}

          <EventLogs
            inventoryLogStore={this.props.inventoryLogStore}
            product={product}
            locationId={locationId}
          />
          <div style={{ padding: '1rem' }} />
        </Drawer>
      </>
    )
  }
}
